import { StudyTimeOptions } from '@/constants/studyTimeOptions';

/** Mirrors WebApi Models.Common.NewViewerBackend.KollelStatus (HAT-1446 phase 5, welfare-committee
 * gate). Draft is unused until a future save-and-resume wizard exists. */
export enum KollelStatus {
  Draft = 'Draft',
  PendingApproval = 'PendingApproval',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

/** One step of a kollel's status timeline, mirroring KollelStatusHistoryEntry. */
export interface KollelStatusHistoryEntryDtoIn {
  status: KollelStatus;
  at: string;
  comment: string | null;
}

export interface KollelContactInfoDtoIn {
  name: string;
  email: string;
  phone: string;
}

export interface KollelAvrechDtoIn {
  supportedId: number;
  firstName: string | null;
  lastName: string | null;
  israeliId: string | null;
  /** Null means this avrech follows the kollel's own studyTime (HAT-1446 phase 3). */
  studyTimeOverride: StudyTimeOptions | null;
}

export interface KollelDtoIn {
  id: number;
  fundId: number;
  /** Populated only by the management endpoints (coordinator lists are already fund-scoped). */
  fundName: string | null;
  name: string;
  address: string | null;
  studyTime: StudyTimeOptions;
  roshKollelContact: KollelContactInfoDtoIn;
  supervisingBodyContact: KollelContactInfoDtoIn;
  status: KollelStatus;
  statusComment: string | null;
  /** Server-computed — drives the admin status dropdown so the UI never hard-codes the workflow. */
  allowedNextStatuses: KollelStatus[];
  /** Oldest first — renders the admin timeline. */
  statusHistory: KollelStatusHistoryEntryDtoIn[];
  notes: string | null;
  /** HAT-1446 phase 4 — the kollel's own per-avrech stipend sub-caps. Null until the coordinator sets them. */
  excellenceLimit: number | null;
  examsLimit: number | null;
  punctualityLimit: number | null;
  avrechs: KollelAvrechDtoIn[];
  createdByEmail: string | null;
  createdAt: string;
  updatedAt: string | null;
}

/** Payload of the kollel-details form (phase 2's edit form, and step 1 of the phase-3 wizard). Shared by create/update. */
export interface KollelFormPayload {
  name: string;
  address: string;
  studyTime: StudyTimeOptions;
  roshKollelContact: KollelContactInfoDtoIn;
  supervisingBodyContact: KollelContactInfoDtoIn;
  notes: string;
  excellenceLimit: number | null;
  examsLimit: number | null;
  punctualityLimit: number | null;
  /** Create only: the wizard's avrechim, saved together with the kollel. Omitted on update. */
  avrechs?: KollelAvrechInputDtoIn[];
}

/** GET funds/{fundId}/kollels/stipend-settings — null means the office has not approved a figure
 * yet (HAT-1446 phase 4), and no total is enforced. */
export interface KollelStipendSettingsDtoIn {
  maxTotalStipendPerAvrech: number | null;
}

/** One avrech row of UpdateKollelAvrechsDtoIn. studyTimeOverride null means "use the kollel's default". */
export interface KollelAvrechInputDtoIn {
  supportedId: number;
  studyTimeOverride: StudyTimeOptions | null;
  /** The coordinator confirmed the "בקשה להעברת כולל" dialog for this avrech (HAT-1446 phase 7) -
   * without it, the server rejects a row for someone already linked to a different kollel. */
  confirmTransfer?: boolean;
}

export interface UpdateKollelAvrechsDtoIn {
  avrechs: KollelAvrechInputDtoIn[];
}

/** PUT management/kollels/{id}/status payload (HAT-1446 phase 5). */
export interface UpdateKollelStatusDtoIn {
  status: KollelStatus;
  /** Optional comment for the status change (e.g. rejection reason), shown to the coordinator. */
  comment: string | null;
}
